/**
 * Bulk course upload (CSV / Excel) with server-side validated preview.
 * Steps: download template -> upload -> preview (row status + errors) -> import valid rows -> summary.
 * Backend: /api/admin/courses/bulk/{template,preview,import}
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Download, Loader2, CheckCircle2, XCircle, Copy, FileSpreadsheet, X } from 'lucide-react';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
const ROW_STYLE = {
  valid: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  duplicate: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  invalid: 'text-red-300 bg-red-500/10 border-red-500/30',
};

export default function CourseBulkUpload({ token, onClose, onImported }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('all');

  const download = async (format) => {
    try {
      const res = await axios.get(`${API}/admin/courses/bulk/template`, { headers, params: { format }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `course_upload_template.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/courses/bulk/preview`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read the file');
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/admin/courses/bulk/import`, { batch_id: preview.batch_id, duplicate_action: duplicateAction }, { headers });
      setResult(res.data);
      toast.success('Import finished');
      onImported && onImported(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const rows = (preview?.rows || []).filter((r) => filter === 'all' || r.status === filter);
  const importable = (preview?.counts?.valid || 0) + (duplicateAction === 'update' ? (preview?.counts?.duplicate || 0) : 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="course-bulk-upload-modal">
      <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col rounded-2xl border border-zinc-800 bg-navigate-dark">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-navigate-gold" /> Bulk upload courses (CSV / Excel)</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="bulk-upload-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-5">
          {/* Step 1 + 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl border border-zinc-800 bg-navigate-surface">
              <p className="text-sm text-white font-medium mb-1">1. Download the template</p>
              <p className="text-xs text-zinc-500 mb-3">Required: title, description, category. Optional: course_code (unique), slug, level, status, visibility, price, currency, duration_hours, cpd_points, instructor_name, tags (separate with ;), thumbnail_url, nqf_level, method.</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-200" onClick={() => download('xlsx')} data-testid="bulk-template-xlsx"><Download className="h-4 w-4" /> Excel</Button>
                <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-200" onClick={() => download('csv')} data-testid="bulk-template-csv"><Download className="h-4 w-4" /> CSV</Button>
              </div>
            </div>
            <div className="p-4 rounded-xl border border-zinc-800 bg-navigate-surface">
              <p className="text-sm text-white font-medium mb-1">2. Upload your file</p>
              <p className="text-xs text-zinc-500 mb-3">Nothing is saved until you confirm the import.</p>
              <div className="flex flex-wrap gap-2 items-center min-w-0">
                <input type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} className="text-xs text-zinc-300 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-800 file:px-3 file:py-2 file:text-zinc-200" data-testid="bulk-upload-file" />
                <Button size="sm" onClick={runPreview} disabled={!file || previewing} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="bulk-upload-preview-btn">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Preview
                </Button>
              </div>
            </div>
          </div>

          {/* Step 3 - preview */}
          {preview && !result && (
            <div className="space-y-3" data-testid="bulk-upload-preview">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-white font-medium mr-2">3. Review {preview.total} rows</span>
                {['all', 'valid', 'duplicate', 'invalid'].map((k) => (
                  <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full border text-xs capitalize ${filter === k ? 'border-navigate-gold text-navigate-gold' : 'border-zinc-700 text-zinc-400'}`} data-testid={`bulk-filter-${k}`}>
                    {k} {k === 'all' ? preview.total : preview.counts?.[k] || 0}
                  </button>
                ))}
              </div>
              {(preview.counts?.duplicate || 0) > 0 && (
                <div className="flex items-center gap-3 text-sm p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                  <Copy className="h-4 w-4 text-amber-400" />
                  <span className="text-zinc-300">{preview.counts.duplicate} rows match existing courses (by course code, slug or title).</span>
                  <select value={duplicateAction} onChange={(e) => setDuplicateAction(e.target.value)} className="h-8 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-xs" data-testid="bulk-duplicate-action">
                    <option value="skip">Skip duplicates</option>
                    <option value="update">Update existing courses</option>
                  </select>
                </div>
              )}
              <div className="rounded-xl border border-zinc-800 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-navigate-surface text-zinc-400 uppercase">
                    <tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Title</th><th className="p-2 text-left">Category</th><th className="p-2 text-left">Code</th><th className="p-2 text-left">Publish</th><th className="p-2 text-left">Issues</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.row_number} className="border-t border-zinc-800 align-top" data-testid={`bulk-row-${r.row_number}`}>
                        <td className="p-2 text-zinc-500">{r.row_number}</td>
                        <td className="p-2"><span className={`px-2 py-0.5 rounded-full border capitalize ${ROW_STYLE[r.status]}`}>{r.status}</span></td>
                        <td className="p-2 text-white max-w-[220px]">{r.data?.title || <span className="text-zinc-600">-</span>}</td>
                        <td className="p-2 text-zinc-300">{r.data?.category}</td>
                        <td className="p-2 text-zinc-400 font-mono">{r.data?.course_code}</td>
                        <td className="p-2 text-zinc-400 capitalize">{r.data?.status} / {r.data?.visibility}</td>
                        <td className="p-2 text-zinc-300 max-w-[320px]">
                          {r.errors?.length ? <ul className="text-red-300 list-disc pl-4">{r.errors.map((e) => <li key={e}>{e}</li>)}</ul> : null}
                          {r.existing && <div className="text-amber-300">Matches “{r.existing.title}” ({r.existing.matched_on})</div>}
                          {r.ignored_columns?.length ? <div className="text-zinc-500">Ignored columns: {r.ignored_columns.join(', ')}</div> : null}
                          {!r.errors?.length && !r.existing && <span className="text-emerald-400">Ready</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 4 - summary */}
          {result && (
            <div className="space-y-3" data-testid="bulk-upload-summary">
              <p className="text-white font-medium">Import summary</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[['created', 'text-emerald-300'], ['updated', 'text-blue-300'], ['skipped', 'text-amber-300'], ['failed', 'text-red-300']].map(([k, c]) => (
                  <div key={k} className="p-4 rounded-xl border border-zinc-800 bg-navigate-surface" data-testid={`bulk-summary-${k}`}>
                    <div className={`text-2xl font-bold ${c}`}>{result.summary?.[k] || 0}</div>
                    <div className="text-xs text-zinc-400 capitalize">{k}</div>
                  </div>
                ))}
              </div>
              {(result.results || []).filter((r) => r.result === 'failed' || r.result === 'skipped').length > 0 && (
                <div className="rounded-xl border border-zinc-800 p-3 text-xs space-y-1 max-h-60 overflow-y-auto">
                  <p className="text-zinc-400 mb-1">Rows to correct and re-upload:</p>
                  {(result.results || []).filter((r) => r.result === 'failed' || r.result === 'skipped').map((r) => (
                    <div key={r.row_number} className="flex gap-2">
                      {r.result === 'failed' ? <XCircle className="h-4 w-4 text-red-400 shrink-0" /> : <CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0" />}
                      <span className="text-zinc-300">Row {r.row_number}: {r.result} - {r.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-zinc-800">
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {preview && !result && (
            <Button onClick={runImport} disabled={importing || importable === 0} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="bulk-upload-import-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import {importable} row{importable === 1 ? '' : 's'}{preview.counts?.invalid ? ` (skip ${preview.counts.invalid} invalid)` : ''}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
